/*=== CORE IMPORTS ===*/
import { LitElement, html } from 'lit';
import { auth, signOut } from './firebaseConfig.js';
import page from 'page';
import { setupRoutes, appRender } from './routes.js';
import * as exports from './views/views.js';
import { shoelaceStyles } from './styles/shoelace-styles.js';
import { q1HomeStyles } from './q1-home-styles.js';
import { q1Vars } from './styles/q1-vars.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';

/*=== LIBRARY MODULES ===*/
import { registerIconLibrary } from '@shoelace-style/shoelace/dist/utilities/icon-library.js';

/*=== Q1 MODULES ===*/
import { q1MemberFunctions } from './modules/q1-core-functions.js';
import { CacheManager } from './modules/cache-manager.js';
import { TutorialManager } from './modules/tutorial-manager.js';

/*=== CONSTANTS ===*/

const STANDARD_CACHE_REFRESH_INTERVAL = 2; // in seconds

const MEMBER_INBOX_CACHE_ID = (memberId) => `member-inbox:${memberId}`;

const INBOX_CACHE_MAX_AGE = 1; // in minutes

/*=== Q1 HOME COMPONENT ===*/

class Q1Home extends LitElement {
  static styles = [
    q1Vars,
    shoelaceStyles,
    q1HomeStyles,
  ];

  static get properties() {
    return {
      currentUser: { type: Object },
      currentMember: { type: Object },
      isSignedIn: { type: Boolean },
      isAdmin: { type: Boolean },

      currentRoute: { type: String },
      routeParams: { type: Object },
      pageRouter: { type: Object },
      pageInstance: { type: Object },
      
      cacheManager: { type: Object },
      tutorialManager: { type: Object },

      inboxCache: { type: Object },
      inboxCacheRefreshing: { type: Boolean },
      inboxCacheRefreshedAt: { type: Number },

      // IMPROVEME: This is a workaround. Search file for details.
      toastStack: { type: Object },
      isLoaded: { type: Boolean},
    };
  }

  constructor() {
    super();

    this.currentUser = null;
    this.currentMember = null;
    this.isSignedIn = false;
    this.isAdmin = false;

    this.currentRoute = '';
    this.routeParams = {};
    this.pageRouter = page;
    this.pageInstance = null;
    
    this.cacheManager = new CacheManager(); // Doesn't have cost or dependency so do it right away
    this.tutorialManager = null;

    this.inboxCache = null;
    this.inboxCacheRefreshing = false;
    this.inboxCacheRefreshedAt = null;

    this.toastStack = null;
    this.isLoaded = false;
  }

  connectedCallback() {
    super.connectedCallback();

    this._initAuth();
    this._registerIconLibraries();
    this.addEventListener('sign-out', this.handleSignOut);
  }

  firstUpdated() {
    // Listen for click events so that we can potentially send them to pageRouter.
    // This is needed because the default listeners of Page.js don't detect certain events.
    this.renderRoot.addEventListener('click', this.handleClick.bind(this));
    
    // Other listeners
    this.renderRoot.addEventListener('refresh-current-member', () => this.refreshCurrentMember());
    this.renderRoot.addEventListener('show-toast', (event) => {
      const { message, title, variant, duration } = event.detail;
      this.showToast(message, title, variant, duration);
    });

    this.toastStack = this.renderRoot.querySelector('.sl-toast-stack');
    this._showAndClearQueuedToast();
  }

  _initAuth() {
    const { getCurrentMember } = q1MemberFunctions;

    auth.onAuthStateChanged((user) => {
      if (user) {
        this.isSignedIn = true;
        this.currentUser = user;

        getCurrentMember().then((response) => {
          const responseMemberData = response.data?.memberData;

          if (!responseMemberData?.id) {
            console.error('onAuthStateChanged: Invalid getCurrentMember response.', response);
            this.isSignedIn = false;
            this.showToast('Error loading membership details', 'Q1 App Load Error', 'danger');
          } else {
            this.currentMember = this._enrichMemberData(responseMemberData);
            this.isAdmin = ['admin', 'super'].includes(this.currentMember.adminLevel);
            this._initTutorialManager();
            this.refreshInboxCache(); // don't await
          }
          
          this.pageInstance = setupRoutes(page, window);
          this.isLoaded = true;
        }).catch((error) => {
          console.error('onAuthStateChanged: Error from getCurrentMember.', error);
          this.showToast(error.message, 'Q1 App Load Error', 'danger');
          
          this.isSignedIn = false;
          this.pageInstance = setupRoutes(page, window);
          this.isLoaded = true;
        });
      } else {
        this.isSignedIn = false;
        this.currentUser = null;
        this.currentMember = null;

        this.pageInstance = setupRoutes(page, window);
        this.isLoaded = true;
      }
    });
  }

  _initTutorialManager() {
    try {
      if (this.tutorialManager?.queryTutorials) {
        // It already exist, so just redo the query
        this.tutorialManager.currentMember = this.currentMember;
        this.tutorialManager.queryTutorials();
      } else {
        this.tutorialManager = new TutorialManager(this.currentMember);
      }
    } catch (error) {
      console.error('Error loading tutorialManager:', error);
    }
  }

  async refreshInboxCache() {
    if (!this.currentMember?.id || this.inboxCacheRefreshing) return;

    this.inboxCacheRefreshing = true;
    const memberInboxCacheId = MEMBER_INBOX_CACHE_ID(this.currentMember.id);
    const isFirstLoad = !this.inboxCache;

    // On first load, get the cached version if it exists, before hitting the server.
    // After that, this is coming from the member clicking refresh, so go to server directly.
    this.inboxCache = isFirstLoad
      ? await this.cacheManager.getCache(memberInboxCacheId, INBOX_CACHE_MAX_AGE)
      : await this.cacheManager.refreshLocalCache(memberInboxCacheId);

    this.inboxCacheRefreshedAt = this.inboxCache.refreshedAt;
    this.dispatchEvent(new CustomEvent('inbox-cache-refreshed', {
      bubbles: true, composed: true, detail: { inboxCache: this.inboxCache },
    }));
    
    if (this.inboxCache.isNewOrStale) {
      this.inboxCache = await this.inboxCache.refreshComplete;
      this.inboxCacheRefreshedAt = this.inboxCache?.refreshedAt;
      this.dispatchEvent(new CustomEvent('inbox-cache-refreshed', {
        bubbles: true, composed: true, detail: { inboxCache: this.inboxCache },
      }));
    }
    
    this.inboxCacheRefreshing = false;
  }

  _registerIconLibraries() {
    registerIconLibrary('material', {
      resolver: name => {
        return `/assets/icons/material/outlined/${name}.svg`;
      },
      mutator: svg => svg.setAttribute('fill', 'currentColor')
    });
    
    registerIconLibrary('q1', {
      resolver: name => {
        return `/assets/icons/q1/${name}.svg`;
      },
      mutator: svg => svg.setAttribute('fill', 'currentColor')
    });
    
    registerIconLibrary('vectors', {
      resolver: name => {
        return `/assets/icons/vectors/${name}.svg`;
      },
      mutator: svg => svg.setAttribute('fill', 'currentColor')
    });

    registerIconLibrary('tabler', {
      resolver: name => {
        return `/assets/icons/tabler/${name}.svg`;
      },
    });
  }

  handleSignOut() {
    signOut(auth).then(() => {
      this.isSignedIn = false;
      this.currentUser = undefined;
      this.navigateTo('/');
      
      this.showToast(
        'You have been signed out of the q1 network.', 'Sign out successful.', 'neutral'
      );

      this.isLoaded = true;
    }).catch((error) => {
      console.error('Sign out error:', error);
    });
  }

  async refreshCurrentMember() {
    const { getCurrentMember } = q1MemberFunctions;

    try {
      const response = await getCurrentMember({});
      const responseMemberData = response.data?.memberData;

      if (!responseMemberData?.id) {
        console.error('refreshCurrentMember: Invalid response.', response);
      } else {
        this.currentMember = this._enrichMemberData(responseMemberData);
        this.isAdmin = ['admin', 'super'].includes(this.currentMember.adminLevel);
        this._initTutorialManager();
        this.refreshInboxCache(); // don't await
      }

      return this.currentMember;
    } catch (error) {
      console.error('refreshCurrentMember: Error.', error);      
    }
  }

  _enrichMemberData(memberData) {
    if (memberData.createdAt) memberData.createdAt = new Date(memberData.createdAt);
    if (memberData.updatedAt) memberData.updatedAt = new Date(memberData.updatedAt);

    const joinDate = memberData.createdAt || new Date();
    const now = new Date();
    memberData.membershipDays = Math.floor((now - joinDate) / (1000*60*60*24))

    return memberData;
  }

  _escapeHtml(html) {
    const div = document.createElement('div');
    div.textContent = html;
    return div.innerHTML;
  }

  /**
   * Use this to navigate to a particular path (`/projects/123`, `/home`, etc) from code.
   * Do not use this for direct user-click-driven navigation. For that just use `<a href="...">`.
   * This is an alternative to `window.location.href = '...'` which uses the app router and 
   * avoids a full page reload.
   * 
   * @param {string} path - The path to navigate to.
   * @param {Event} event - Optional. If included, checks for pressing of CMD/CTRL/SHIFT and opens
   *   the path in a new tab/window if so.
  */
  navigateTo(path, event = null) {
    // Open in a new tab if user is holding down CMD/CTRL/SHIFT
    if (event && path && (event.metaKey || event.ctrlKey || event.shiftKey)) {
      window.open(path, '_blank');
      return;
    }

    if (this.pageRouter && path) {
      if (path.startsWith('/')) this.pageRouter(path);
      else window.location.href = path;
    }
  }

  /**
   * Adds a toast alert to the toast stack using `<sl-alert>`
   * 
   * @param {*} message - The message to display.
   * @param {*} title - Optional. If included, this is added as the first line, in bold.
   * @param {string} variant - `primary`, `success`, `neutral`, `warning`, `danger`
   * @param {*} duration - Time to display in milliseconds.
   */
  showToast(message, title = undefined, variant = 'primary', duration = 3000) {
    const iconMap = {
      primary: 'info',
      success: 'task_alt',
      neutral: 'settings',
      warning: 'warning',
      danger: 'report',
    };
    const icon = iconMap[variant] || 'info';

    const alert = Object.assign(document.createElement('sl-alert'), {
      variant,
      closable: true,
      duration: duration,
      innerHTML: `
        <sl-icon library="material" name="${icon}" slot="icon"></sl-icon>
        ${!title ? '' : `<strong>${title}</strong><br/>`}
        ${this._escapeHtml(message)}
      `
    });

    /*
      IMPROVEME: Workaround. Shoelace creates the toast stack in root document where it is 
      unstyled. We should find a better way to handle this or create a PR for shoelace.
      
      Refer to source code here...
      https://github.com/shoelace-style
      /shoelace/blob/next/src/components/alert/alert.component.ts#L195
    */
    
    alert.toast(); // This causes the alert to be reparented to toast stack in root document
    this.toastStack.appendChild(alert); // We reparent to our own toast stack
    
    return alert;
  }

  /**
   * Saves a toast message to display the next time the page loads.
   * Used to persist toast messages across page navs which trigger a reload.
   * NOTE: This implementation only supports one queued toast at a time.
   */
  queueToast(message, title, variant, duration) {
    const toastParams = { message, title, variant, duration };

    localStorage.setItem('queuedToast', JSON.stringify(toastParams));
  }

  _showAndClearQueuedToast() {
    const toastParams = JSON.parse(localStorage.getItem('queuedToast')) || {};

    if (toastParams?.message) {
      this.showToast(
        toastParams.message,
        toastParams.title,
        toastParams.variant,
        toastParams.duration
      );

      localStorage.removeItem('queuedToast');
    }
  }

  /**
   * This listens to all click events within the app and captures those triggered by anchor link
   * elements (unless the keyboard state indicates the desire to open in a new tab/window).
   * Captured events are prevented from triggering browser navigation and are instead directed
   * to `this.navigateTo()`.
   */
  handleClick(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey) return; // open in new tab/window
    if (event.defaultPrevented) return; // already handled elsewhere

    const target = event.target;
    const originalTarget = event.composedPath()[0];
    const anchor = originalTarget.closest('a');

    if (
      anchor && anchor.pathname
      && (anchor.hostname === window.location.hostname)
      && (anchor.target !== '_blank')
    ) {
      event.preventDefault();
      this.navigateTo(anchor.pathname + anchor.search);
    }
  }

  /*=== GETTERS ===*/

  /**
   * The app persona is a standardized way to determine the context of the current app viewer.
   * - `public` - Not signed in
   * - `network` - Signed in as network level member
   * - `contributor` - Signed in as contributor level member
   * - `foundation` - Signed in as foundation level member
   * - `steward` - Signed in as steward level member
   */
  get appPersona() {
    if (!this.currentMember?.level) return 'public';
    return this.currentMember.level;
  }

  /*=== RENDER ===*/

  render() {
    return html`
      ${!this.isLoaded ? null : appRender({
        currentRoute: this.currentRoute,
        routeParams: this.routeParams,
        currentUser: this.currentUser,
        currentMember: this.currentMember,
        isSignedIn: this.isSignedIn,
        isAdmin: this.isAdmin,
      })}

      <div class="sl-toast-stack"></div>
    `;
  }
}

customElements.define('q1-home', Q1Home);

export default Q1Home;
